import { AccountType, FeedbackId, InstrumentPrices } from '@/common/types'
import { Dialog, DialogContent, DialogFooter, DialogTrigger } from '@/common/ui/dialogMobileSupport'
import { NoKycWarning } from '@/kyc/noKycWarning'
import { NoTradingAccountAlert } from '@/trading/noTradingAccountAlert'
import { OrderReview, OrderReviewButtons } from './orderReview'
import { OrderForm, OrderFormButtons } from './orderForm'
import { Button } from '@/common/ui/button'
import { Tabs, TabsContent } from '@/common/ui/tabs'
import { BUNDLE_COPY } from '@/locales/en'
import { BundleExitIntentDialog } from './bundleExitIntentDialog'
import { useCallback, useEffect, useRef, useState } from 'react'
import useBundleTrade from './useBundleTrade'
import { useFetchKYC } from '@/kyc/kycQueries'
import { useFetchTradingAccount } from '@/trading/tradingQueries'
import useUserStore from '@/user/userStore'
import useAccountStore from '@/account/accountStore'
import { useCurrentAccount } from '@/account/useCurrentAccount'
import { OrderConfirmation, OrderConfirmationButtons } from './orderConfirmation'
import { useOrderSource } from '@/common/context/useOrderSource'

type Props = {
    resetForm: () => void
    originalAmount: number
    instrumentsForForm: {
        securityCode: string
        securityName: string
        description: string
        priceInfo: InstrumentPrices
        weight: number
    }[]
    open: boolean
    onOpenChange: (open: boolean) => void
}

export const BundleModal: React.FC<Props> = ({ resetForm, originalAmount, instrumentsForForm, open, onOpenChange }) => {
    const { clearOrderSource } = useOrderSource()
    const [activeTab, setActiveTab] = useState<'trade' | 'review'>('trade')
    const [exitIntentOpen, setExitIntentOpen] = useState(false)
    const [orderStatus, setOrderStatus] = useState<'trade' | 'review' | 'confirmation'>('trade')
    const [hasError, setHasError] = useState<boolean>(false)

    const { user } = useUserStore()
    const account = useCurrentAccount()
    const activeAccountId = useAccountStore((s) => s.activeAccountId)
    const { data: kyc } = useFetchKYC(activeAccountId, { enabled: true })
    const { data: tradingAccount } = useFetchTradingAccount(activeAccountId)
    const hasApprovedTradingAccount = tradingAccount?.status === 'APPROVED'
    const isKYCPending = kyc?.status && kyc?.status !== 'VERIFICATION_SUCCESS'

    const {
        orderItems: lines,
        hasFundsError,
        totalInvestmentAmount,
        isSubmitting,
        resetState,
        bundleId,
        totals,
    } = useBundleTrade()

    const _initialTabRef = useRef(true)
    const dialogContentRef = useRef<HTMLDivElement | null>(null)

    const onOrderStatusChange = (status: 'trade' | 'review' | 'confirmation') => {
        setActiveTab(status === 'confirmation' ? 'review' : status)
        setOrderStatus(status)
    }

    useEffect(() => {
        if (_initialTabRef.current) {
            _initialTabRef.current = false
            return
        }

        // Try to scroll the dialog's internal scroll container first
        const dialogEl = dialogContentRef.current
        const scrollContainer = dialogEl?.querySelector<HTMLElement>('.overflow-y-auto')

        if (scrollContainer) {
            scrollContainer.scrollTo({ top: 0 })
            return
        }

        // Fallback to window
        window.scrollTo({ top: 0 })
    }, [activeTab])

    useEffect(() => {
        const newError = hasFundsError || lines.some((line) => line.hasAmountError || line.hasLimitPriceError)
        setHasError(newError)
    }, [hasFundsError, lines])

    const closeOrderForm = useCallback(() => {
        if (isSubmitting) return
        onOpenChange(false)
        clearOrderSource()
        if (orderStatus === 'confirmation') {
            resetState()
            resetForm()
        } else if (
            user?.feedbackFlags?.[FeedbackId.BUNDLE_EXIT_INTENT] !== true &&
            !isKYCPending &&
            hasApprovedTradingAccount
        ) {
            setExitIntentOpen(true)
        }
        setOrderStatus('trade')
        setActiveTab('trade')
    }, [
        isSubmitting,
        onOpenChange,
        orderStatus,
        resetState,
        resetForm,
        user?.feedbackFlags,
        isKYCPending,
        hasApprovedTradingAccount,
        setExitIntentOpen,
        clearOrderSource,
    ])

    return (
        <>
            <Dialog open={open} onOpenChange={(open) => (!open ? closeOrderForm() : onOpenChange(open))}>
                <DialogTrigger asChild>
                    <div className="fixed bottom-0 left-0 w-full z-50 md:hidden">
                        <div className="w-full self-stretch h-16 px-4 py-3 bg-white shadow-[0px_4px_16px_0px_rgba(0,0,0,0.04)] border-t border-Border-Outline inline-flex justify-start items-start gap-2.5">
                            <Button
                                variant={'secondary'}
                                size="lg"
                                className="w-full"
                                onClick={() => onOpenChange(true)}
                            >
                                {BUNDLE_COPY.buy}
                            </Button>
                        </div>
                    </div>
                </DialogTrigger>
                {isKYCPending ? (
                    <DialogContent
                        ref={dialogContentRef}
                        open={open}
                        onClose={closeOrderForm}
                        className={`sm:min-h-[50vh] max-w-[680px] overflow-y-auto sm:px-10`}
                    >
                        <NoKycWarning onKycClick={() => {}} kycProviderEntityId={kyc.kycProviderEntityId} />
                    </DialogContent>
                ) : !hasApprovedTradingAccount ? (
                    <DialogContent
                        ref={dialogContentRef}
                        open={open}
                        onClose={closeOrderForm}
                        className={`sm:min-h-[50vh] max-w-[680px] overflow-y-auto sm:px-10`}
                    >
                        <NoTradingAccountAlert
                            swiftIdOnboarding={
                                (account?.accountType ?? user?.accountType) &&
                                [AccountType.COMPANY_TRUST, AccountType.INDIVIDUAL_TRUST, AccountType.COMPANY].includes(
                                    account?.accountType ?? user?.accountType ?? AccountType.INDIVIDUAL,
                                )
                            }
                        />
                    </DialogContent>
                ) : (
                    <DialogContent
                        ref={dialogContentRef}
                        open={open}
                        onClose={closeOrderForm}
                        onBack={activeTab === 'review' ? () => onOrderStatusChange('trade') : undefined}
                        className={`sm:min-h-[50vh] max-w-[600px] overflow-y-auto  sm:h-[90vh]`}
                    >
                        {['trade', 'review'].includes(orderStatus) ? (
                            <Tabs value={activeTab} onValueChange={(v) => onOrderStatusChange(v as 'trade' | 'review')}>
                                <TabsContent value="trade">
                                    <OrderForm
                                        instruments={instrumentsForForm}
                                        hasHIN={true}
                                        onClose={closeOrderForm}
                                        resetForm={resetForm}
                                    />
                                </TabsContent>

                                <TabsContent value="review">
                                    <OrderReview />
                                </TabsContent>
                            </Tabs>
                        ) : (
                            <OrderConfirmation />
                        )}

                        <DialogFooter>
                            <div className="w-full self-stretch inline-flex flex-col justify-start items-start bg-white">
                                {['trade', 'review'].includes(orderStatus) ? (
                                    <>
                                        <div className="sm:hidden self-stretch inline-flex justify-start items-center gap-1">
                                            <div
                                                className={`flex-1 h-2 bg-brand rounded-tr-[40px] rounded-br-[40px]`}
                                            />
                                            <div
                                                className={`flex-1 h-2 ${orderStatus === 'review' ? 'bg-brand' : 'bg-brand-dust'} rounded-tl-[40px] rounded-bl-[40px]`}
                                            />
                                        </div>
                                        <div className="w-full self-stretch px-4 py-3">
                                            {orderStatus === 'trade' ? (
                                                <OrderFormButtons
                                                    bundleId={bundleId ?? ''}
                                                    resetOnClick={resetForm}
                                                    reviewOnClick={() => onOrderStatusChange('review')}
                                                    resetDisabled={originalAmount === totalInvestmentAmount}
                                                    reviewDisabled={hasError || totals.totalAmount === 0}
                                                />
                                            ) : (
                                                <OrderReviewButtons
                                                    onBack={() => onOrderStatusChange('trade')}
                                                    onSubmit={() => onOrderStatusChange('confirmation')}
                                                />
                                            )}
                                        </div>
                                    </>
                                ) : (
                                    <div className="w-full self-stretch px-4 py-3">
                                        <OrderConfirmationButtons onClose={closeOrderForm} />
                                    </div>
                                )}
                            </div>
                        </DialogFooter>
                    </DialogContent>
                )}
            </Dialog>

            <BundleExitIntentDialog
                open={exitIntentOpen}
                onClose={() => setExitIntentOpen(false)}
                bundleId={bundleId ?? ''}
            />
        </>
    )
}
